import { useEffect, useMemo, useState } from "@wordpress/element";
import { ZoomControl } from "@/components/map-container/components/ZoomControl";
import { fetchArea } from "@/services/fetch-area";
import { IAreaData } from "@/components/map-container/types/map-type";
import { useSelect } from "@wordpress/data";
import { WeatherSelect } from "@/store/type";
import { WEATHER_STORE } from "@/store/store";
import { formatAreaData } from "../helpers/format-area-data";
import { useWindowSize } from "@/hooks/use-window-resize";
import { compareIosDate } from "@/helpers/format-date";
import { getHighestWarningForTodayAndTomorrow } from "../helpers/get-highest-value-warning";

import MapDetails from "./map-details/MapDetails";
import MapContainer from "@/components/map-container/MapContainer";
import WarningMapList from "./warning-map-list/WarningMapList";
import MapControlWarnings from "./map-control-warnings/MapControlWarnings";

import "./style.scss";

export const App = () => {
	const width = useWindowSize();
	const [selectedDate, setSelectDate] = useState<Date>(new Date());

	const [selectedPolygonId, setSelectedPolygonId] = useState<
		string | undefined
	>(undefined);
	const [selectedControlFilter, setSelectedFilter] = useState<
		string | undefined
	>(undefined);

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [areaData, setAreaData] = useState<{
		isLoading: boolean;
		error: string | null;
		data: IAreaData[];
	}>({
		isLoading: true,
		error: null,
		data: [],
	});

	const { warningData, isLoading, error } = useSelect(
		(select: (store: typeof WEATHER_STORE) => WeatherSelect) =>
			select(WEATHER_STORE).getWarningState(),
	);

	useEffect(() => {
		async function fetchData() {
			const res = await fetchArea();
			setAreaData(res);
		}

		fetchData();
	}, []);

	const warningDataFiltred = useMemo(() => {
		return warningData.filter((warning) =>
			compareIosDate(new Date(warning.properties.effective_str), selectedDate),
		);
	}, [warningData, selectedDate]);

	const { today: maxToday, tomorrow: maxTomorrow } = useMemo(() => {
		return getHighestWarningForTodayAndTomorrow(warningData);
	}, [warningData]);

	const areaPolygon = useMemo(() => {
		return formatAreaData({
			areaData: areaData.data,
			warningData: warningDataFiltred,
			filter: selectedControlFilter,
		});
	}, [areaData, selectedControlFilter, warningDataFiltred]);

	return (
		<section className="warnings-map-container">
			<WarningMapList
				selectedDate={selectedDate}
				handleSelectDate={(date: Date) => setSelectDate(date)}
				maxToday={maxToday}
				maxTomorrow={maxTomorrow}
			/>

			<div className="warning-map-content">
				<MapControlWarnings
					setSelectedFilter={(value?: string) => setSelectedFilter(value)}
				/>
				<MapDetails
					selectedPolygonId={selectedPolygonId}
					isOpen={isOpen}
					toggleOpen={() => setIsOpen((prev) => !prev)}
					warningData={warningDataFiltred}
					isLoading={isLoading}
					error={error}
					filter={selectedControlFilter}
				/>

				<MapContainer
					allowInteraction={true}
					showRegionStroke={true}
					selectedPolygonId={selectedPolygonId}
					controls={[new ZoomControl(false)]}
					height={654}
					zoom={width > 680 ? 8 : 7.2}
					areaPolygon={areaPolygon}
					showStationLabelAndImage={false}
					clickOnPolygon={(open: boolean, value?: string) => {
						setSelectedPolygonId(value);
						setIsOpen(open);
					}}
				/>
			</div>
		</section>
	);
};

export default App;
